import produce from 'immer';
import _ from 'underscore';

import {ReviewCriteriaActionType} from 'notebook/reviews/ReviewCriteriaReduxActions';
import {BenchlingAction} from 'redux-stores/Action';
import {BenchlingState} from 'redux-stores/reduceApp';

import {ReviewCriterion, SavedReviewCriteriaGroup, SavedReviewCriterion} from './ReviewCriteriaConstants';

export interface StoreReviewCriteriaGroup {
  id: string;
  name: string;
  reviewCriteria: Array<string>;
}

export interface ReviewCriteriaState {
  reviewCriteriaGroupIdsByLinkedItemId: {[linkedItemId: string]: Array<string>};
  reviewCriteriaGroupsById: {[groupId: string]: StoreReviewCriteriaGroup};
  reviewCriteriaById: {[id: string]: SavedReviewCriterion};
}

export const REVIEW_CRITERIA_INITIAL_STATE = {
  reviewCriteriaGroupIdsByLinkedItemId: {},
  reviewCriteriaGroupsById: {},
  reviewCriteriaById: {},
};

export function mapReviewCriteriaGroupsToProps(
  state: BenchlingState,
  linkedItemId: string
): Array<{id: string | undefined; name: string; reviewCriteria: Array<ReviewCriterion>}> | null {
  const {reviewCriteriaById, reviewCriteriaGroupsById, reviewCriteriaGroupIdsByLinkedItemId} =
    state.models.reviews;

  const groupIds = reviewCriteriaGroupIdsByLinkedItemId[linkedItemId];
  if (!groupIds) {
    return null;
  }

  return groupIds.map((groupId) => {
    const group = reviewCriteriaGroupsById[groupId];
    return {
      id: group.id,
      name: group.name,
      reviewCriteria: group.reviewCriteria.map((criterionId) => reviewCriteriaById[criterionId]),
    };
  });
}

function updateReviewCriteriaGroups(
  state: ReviewCriteriaState,
  reviewCriteriaGroups: Array<SavedReviewCriteriaGroup>
): ReviewCriteriaState {
  return produce(state, (draftState) => {
    for (const criteriaGroup of reviewCriteriaGroups) {
      draftState.reviewCriteriaGroupsById[criteriaGroup.id] = {
        ...criteriaGroup,
        // eslint-disable-next-line no-restricted-syntax
        reviewCriteria: _.pluck(criteriaGroup.reviewCriteria, 'id'),
      };
      for (const criterion of criteriaGroup.reviewCriteria) {
        draftState.reviewCriteriaById[criterion.id] = criterion;
      }
    }
  });
}

export default function reduceReviewCriteria(
  state: ReviewCriteriaState = REVIEW_CRITERIA_INITIAL_STATE,
  action: BenchlingAction
): ReviewCriteriaState {
  switch (action.type) {
    case ReviewCriteriaActionType.REVIEW_CRITERIA_FETCH_COMPLETED: {
      const {linkedItemId, reviewCriteriaGroups} = action;
      state = updateReviewCriteriaGroups(state, reviewCriteriaGroups);
      return produce(state, (draftState) => {
        // eslint-disable-next-line no-restricted-syntax
        draftState.reviewCriteriaGroupIdsByLinkedItemId[linkedItemId] = _.pluck(reviewCriteriaGroups, 'id');
      });
    }
    case ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_CREATE_COMPLETED: {
      const {linkedItemId, savedReviewCriteriaGroup} = action;
      state = updateReviewCriteriaGroups(state, [savedReviewCriteriaGroup]);
      return produce(state, (draftState) => {
        draftState.reviewCriteriaGroupIdsByLinkedItemId[linkedItemId].push(savedReviewCriteriaGroup.id);
      });
    }
    case ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_DELETE_COMPLETED: {
      const {linkedItemId, reviewCriteriaGroupId} = action;
      const deletedGroupCriterionIds = state.reviewCriteriaGroupsById[reviewCriteriaGroupId].reviewCriteria;
      return produce(state, (draftState) => {
        draftState.reviewCriteriaGroupIdsByLinkedItemId[linkedItemId] =
          draftState.reviewCriteriaGroupIdsByLinkedItemId[linkedItemId].filter(
            (groupId) => groupId !== reviewCriteriaGroupId
          );

        delete draftState.reviewCriteriaGroupsById[reviewCriteriaGroupId];

        for (const deletedCriterionId of deletedGroupCriterionIds) {
          delete draftState.reviewCriteriaById[deletedCriterionId];
        }
      });
    }
    case ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_UPDATE_COMPLETED: {
      const {savedReviewCriteriaGroup} = action;
      return updateReviewCriteriaGroups(state, [savedReviewCriteriaGroup]);
    }
    case ReviewCriteriaActionType.REVIEW_CRITERION_UPDATED: {
      const {criterionId, criterion} = action;
      return produce(state, (draftState) => {
        draftState.reviewCriteriaById[criterionId] = criterion;
      });
    }
    default:
      return state;
  }
}
